/**
 * CrewIdentityEditor — Self-service avatar emoji + crew flair editor.
 * iter84.23 — Crew identity polish.
 *
 * Lets a crewmate pick a curated avatar emoji and a one-line flair
 * (think email signature). Used inside YourTinyCrewModal as an inline
 * accordion so the crewmate can edit without leaving the roster view.
 *
 * Curated avatar list mirrors the backend ALLOWED_AVATAR_EMOJIS set in
 * /app/backend/participants.py — the server rejects anything outside
 * the allow-list, so the picker is the only safe way in.
 */
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Save, Sparkles } from "lucide-react";

export const AVATAR_OPTIONS = [
    // Faces
    "😎", "🤓", "🤩", "🥳", "😈", "🤠", "🥸", "🫡", "🦾",
    // Sports
    "⚽", "🏀", "🏈", "⚾", "🎾", "🏐", "🏒", "🥊", "🏆", "🥇",
    // Mascots
    "🐺", "🦁", "🐅", "🦅", "🐉", "🦊", "🐻", "🦈", "🐙", "🦄",
    // Flair-y
    "⚡", "🔥", "💀", "👑", "🎯", "🎲", "🚀", "🌟", "💎", "🎮",
];

const FLAIR_MAX = 60;

export default function CrewIdentityEditor({ participantId, initialAvatar, initialFlair, onSaved }) {
    const [emoji, setEmoji] = useState(initialAvatar || "");
    const [flair, setFlair] = useState(initialFlair || "");
    const [saving, setSaving] = useState(false);

    const dirty = (emoji || "") !== (initialAvatar || "") || (flair || "") !== (initialFlair || "");
    const flairRemaining = FLAIR_MAX - flair.length;
    const flairOver = flairRemaining < 0;

    const save = async () => {
        if (flairOver) return;
        setSaving(true);
        try {
            const updated = await api.updateMyIdentity(participantId, {
                avatar_emoji: emoji,
                crew_flair: flair,
            });
            toast.success("Crew identity saved");
            onSaved?.(updated);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't save identity");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-3" data-testid="crew-identity-editor">
            <div>
                <p className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 mb-1.5">Avatar</p>
                <div className="grid grid-cols-10 gap-1" data-testid="crew-identity-avatar-grid">
                    {/* "No avatar" option — clears the field server-side */}
                    <button
                        type="button"
                        onClick={() => setEmoji("")}
                        data-testid="crew-identity-avatar-none"
                        className={`aspect-square text-xs font-mono flex items-center justify-center border ${
                            !emoji
                                ? "border-cyber bg-cyber/15 text-cyber"
                                : "border-[#222] text-zinc-500 hover:border-zinc-500"
                        }`}
                        title="No avatar"
                    >∅</button>
                    {AVATAR_OPTIONS.map((e) => (
                        <button
                            key={e}
                            type="button"
                            onClick={() => setEmoji(e)}
                            data-testid={`crew-identity-avatar-${e}`}
                            className={`aspect-square text-lg flex items-center justify-center border ${
                                emoji === e
                                    ? "border-cyber bg-cyber/15"
                                    : "border-[#222] hover:border-zinc-500"
                            }`}
                        >{e}</button>
                    ))}
                </div>
            </div>
            <div>
                <label htmlFor="crew-flair-input" className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 mb-1.5 block">
                    Crew flair <span className="text-zinc-600">(one-line signature)</span>
                </label>
                <input
                    id="crew-flair-input"
                    type="text"
                    value={flair}
                    onChange={(e) => setFlair(e.target.value)}
                    placeholder="World Cup bracket buster · taco connoisseur"
                    maxLength={FLAIR_MAX + 10} /* hard limit slightly above soft limit so users see the warning */
                    className="brutal-input w-full text-sm"
                    data-testid="crew-identity-flair-input"
                />
                <p className={`text-[10px] mt-1 ${flairOver ? "text-heat" : "text-zinc-500"}`} data-testid="crew-identity-flair-counter">
                    {flairOver
                        ? `${Math.abs(flairRemaining)} too many — trim to fit ${FLAIR_MAX} chars`
                        : `${flairRemaining} chars left`}
                </p>
            </div>
            <div className="flex items-center justify-between gap-2 pt-1 border-t border-[#222]">
                <p className="text-[10px] text-zinc-500 italic flex items-center gap-1">
                    <Sparkles className="w-3 h-3 text-cyber" />
                    Preview: <span className="text-base mx-1">{emoji || "🙂"}</span>
                    <span className="text-white font-bold">you</span>
                    {flair && <span className="text-zinc-400 ml-1">— {flair.slice(0, FLAIR_MAX)}</span>}
                </p>
                <button
                    type="button"
                    onClick={save}
                    disabled={!dirty || saving || flairOver}
                    data-testid="crew-identity-save-btn"
                    className="brutal-btn-primary text-xs py-1.5 px-3 disabled:opacity-40"
                >
                    <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save"}
                </button>
            </div>
        </div>
    );
}
